import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { deleteScan, fetchUserScans, setSelectedScan } from '../../actions/scan';
import { withCarousel } from '../../utils/hocs/withCarousel';
import useLocalize from '../../utils/hooks/useLocalize';
import CannedScanCard from './ScanCard/CannedScanCard';
import ScanCard from './ScanCard/ScanCard';
import ScanCriteriaPopupModal from './ScanCriteriaPopupModal';

const MyScansList = ({
  scans,
  activeScan,
  onDetailsClick,
  onDeleteClick,
  onScanClick
}) => (
  <>
    {scans.map((scan) => (
      <ScanCard
        key={scan.id}
        scan={scan}
        onDetailsClick={onDetailsClick}
        onDeleteClick={onDeleteClick}
        onClick={() => onScanClick(scan)}
        active={activeScan && activeScan.id === scan.id}
      />
    ))}
  </>
);

MyScansList.propTypes = {
  scans: PropTypes.array.isRequired,
  activeScan: PropTypes.object,
  onDetailsClick: PropTypes.func.isRequired,
  onDeleteClick: PropTypes.func.isRequired,
  onScanClick: PropTypes.func.isRequired
};

const PresetScansList = ({
  scans,
  activeScan,
  getDescription,
  onDetailsClick,
  onScanClick
}) => (
  <>
    {scans.map((scan) => (
      <CannedScanCard
        key={scan.id}
        scan={scan}
        title={scan.name}
        description={getDescription(scan)}
        onDetailsClick={onDetailsClick}
        onClick={() => onScanClick(scan)}
        active={activeScan && activeScan.id === scan.id}
      />
    ))}
  </>
);

PresetScansList.propTypes = {
  scans: PropTypes.array.isRequired,
  activeScan: PropTypes.object,
  getDescription: PropTypes.func.isRequired,
  onDetailsClick: PropTypes.func.isRequired,
  onScanClick: PropTypes.func.isRequired
};

const MyScansCarousel = withCarousel(MyScansList);
const PresetScansCarousel = withCarousel(PresetScansList);

const SavedPresetScansSection = () => {
  const [activeTab, setActiveTab] = useState('preset');
  const [detailPopupOpen, setDetailPopupOpen] = useState(false);
  const [detailScan, setDetailScan] = useState(null);
  const [detailDescription, setDetailDescription] = useState('');
  const isMyScansTab = activeTab === 'myscans';
  const dispatch = useDispatch();
  const localize = useLocalize();
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const userScans = useSelector((state) => state.scans.userScans || []);
  const staticScans = useSelector((state) => state.scans.staticScans || []);
  const activeScanFromStore = useSelector(
    (state) => state.scans.selectedScan || null
  );
  const isLoading = useSelector((state) => state.scans.isLoading || false);

  useEffect(() => {
    dispatch(fetchUserScans());
  }, [dispatch]);

  const handleScanDetailsClick = (scan, description) => {
    setDetailScan(scan);
    setDetailDescription(description || '');
    setDetailPopupOpen(true);
  };

  const handleDetailsPopupClose = () => {
    setDetailPopupOpen(false);
  };

  const handleDetailsPopupExited = () => {
    setDetailScan(null);
    setDetailDescription('');
  };

  const handleDetailsPopupEdit = () => {
    // TODO: Navigate to the scan criteria editor with a follow up story
    handleDetailsPopupClose();
  };

  const handleDeleteScan = (scan) => {
    if (!scan || !scan.id) return;
    dispatch(deleteScan(scan.id));
  };

  const handleScanCardClick = (scan) => {
    if (activeScanFromStore && activeScanFromStore.id === scan.id) {
      return;
    }
    dispatch(setSelectedScan(scan));
  };

  const getScanDescription = (scan) => localize(`desktop.scans.${scan.id}`) || '';

  const renderMyScansContent = () => {
    if (isLoading) {
      return localize('desktop.scans.myscans.construction');
    }

    if (!userScans || userScans.length === 0) {
      return localize('desktop.scans.myscans.empty');
    }

    return (
      <MyScansCarousel
        sectionClass="carousel-section"
        wrapperClass="carousel-wrapper"
        scrollerClass="scan-cards-carousel"
        dotsClass="carousel-dots"
        dotClass="carousel-dot"
        activeDotClass="active"
        deps={[userScans.length]}
        scans={userScans}
        activeScan={activeScanFromStore}
        onDetailsClick={handleScanDetailsClick}
        onDeleteClick={handleDeleteScan}
        onScanClick={handleScanCardClick}
      />
    );
  };

  const renderPresetScansContent = () => {
    if (isLoading) {
      return (
        <div className="placeholder-text">
          {localize('desktop.scans.recommended.construction')}
        </div>
      );
    }

    if (!staticScans || staticScans.length === 0) {
      return (
        <div className="placeholder-text">
          {localize('desktop.scans.recommended.empty')}
        </div>
      );
    }

    return (
      <PresetScansCarousel
        sectionClass="carousel-section"
        wrapperClass="carousel-wrapper"
        scrollerClass="canned-scan-cards-carousel"
        dotsClass="carousel-dots"
        dotClass="carousel-dot"
        activeDotClass="active"
        deps={[staticScans.length]}
        scans={staticScans}
        activeScan={activeScanFromStore}
        getDescription={getScanDescription}
        onDetailsClick={handleScanDetailsClick}
        onScanClick={handleScanCardClick}
      />
    );
  };

  const renderTabContent = () => {
    const hasCards = staticScans && staticScans.length > 0 && !isLoading;
    return (
      <div
        className={`saved-preset-tab-content ${isMyScansTab ? 'with-carousel' : ''} ${!isMyScansTab && hasCards ? 'with-carousel' : ''}`}
      >
        {isMyScansTab ? renderMyScansContent() : renderPresetScansContent()}
      </div>
    );
  };

  return (
    <div className="saved-preset-scans-section">
      <div className="saved-preset-tabs">
        <div className="saved-preset-tabs-header">
          <button
            className={`saved-preset-tab ${activeTab === 'preset' ? 'active' : ''
              }`}
            onClick={() => handleTabChange('preset')}
          >
            {localize('desktop.scans.tab.recommended')}
          </button>
          <button
            className={`saved-preset-tab ${activeTab === 'myscans' ? 'active' : ''
              }`}
            onClick={() => handleTabChange('myscans')}
          >
            {localize('desktop.scans.tab.myscans')}
          </button>
        </div>
        {renderTabContent()}
      </div>
      <ScanCriteriaPopupModal
        open={detailPopupOpen}
        onClose={handleDetailsPopupClose}
        onExited={handleDetailsPopupExited}
        scan={detailScan}
        description={detailDescription}
        onEdit={handleDetailsPopupEdit}
      />
    </div>
  );
};

export default SavedPresetScansSection;
